/** @jsx isolateComponent */
import { Fragment } from 'react';
import { isolateComponent } from '@zapier/style-encapsulation';
import { Heading } from '../../display/Heading';
import { Icon } from '../../display/Icon';
import { Styles, typeConfigs } from './Styles';
import type { ColorName } from '../../../theme';
import type { IconName } from '../../display/Icon';
import { useTheme } from '../../../index';

export type AlertType = 'info' | 'success' | 'warning' | 'error';

export type Props = {
  /**
   * Controls the `aria-live` attribute of `Alert`.
   */
  ariaLive?: 'off' | 'polite' | 'assertive';
  /**
   * Controls the `role` attribute of `Alert`.
   */
  role?: 'alert' | 'alertdialog';
  /**
   * Controls the styling of `Alert` to indicate to users what type
   * of alert it is.
   */
  type?: AlertType;
  /**
   * Whether or not `Alert` has a drop shadow.
   */
  hasShadow?: boolean;
  /**
   * Controls how content within `Alert` is laid out.
   */
  layout?: 'box' | 'banner' | 'banner-inverse' | 'modal';

  /**
   * The title of the `Alert`.
   */
  title?: React.ReactNode;
  /**
   * The content to render within `Alert`. Typically comprised of
   * subcomponents like `AlertDescription` and `ButtonGroup`.
   */
  children?: React.ReactNode;
  /**
   * Optional function to call when `Alert` is dismissed.
   * Its presence determines whether the close button renders.
   */
  onDismiss?: (e: React.MouseEvent) => void;
};

export type Configs = {
  [key in AlertType]: {
    backgroundColor: ColorName;
    color: ColorName;
    icon: IconName;
  };
};

const defaultProps = {
  hasShadow: true,
  type: 'info',
  layout: 'box',
};

/**
 * `Alert` renders information that is immediately or near-immediately
 * important for users to know.
 */
export const Alert = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };
  const { isRebrand } = useTheme();

  const config = typeConfigs(isRebrand)[props.type as AlertType];
  const isBanner =
    props.layout === 'banner' || props.layout === 'banner-inverse';
  const icon = (
    <div css={Styles.typeIcon(props, isRebrand)}>
      <Icon
        ariaLabel={props.type}
        isBlock={true}
        name={config.icon}
        size={24}
      />
    </div>
  );
  const closeButton = props.onDismiss && (
    <button
      type="button"
      aria-label="Dismiss"
      css={Styles.closeButton(props, isRebrand)}
      onClick={props.onDismiss}
    >
      <Icon isBlock={true} name="formX" size={24} />
    </button>
  );
  // Use `&nbsp;` to prevent `icon` from wrapping and becoming a widow/orphan.
  const bannerIcon = isBanner && icon && <Fragment>{icon}&nbsp;</Fragment>;
  return (
    <article
      aria-live={props.ariaLive}
      css={Styles.root(props, isRebrand)}
      role={props.role}
    >
      {!isBanner && icon}
      <div css={Styles.content}>
        {props.title && (
          <Heading css={Styles.title}>
            <span css={Styles.titleInner(props, isRebrand)}>
              {bannerIcon}
              {props.title}
            </span>
          </Heading>
        )}
        {props.children}
      </div>
      {closeButton}
    </article>
  );
};
